'use client'

import { ProfileProvider } from '@/contexts/ProfileContext'
import { MetalRatesProvider } from '@/contexts/MetalRatesContext'
import { ModeProvider } from '@/contexts/ModeContext'
import { TenantProvider } from '@/contexts/TenantContext'
import { AuthProvider } from '@/contexts/AuthContext'
import { ThemeProvider } from 'next-themes'
import { Toaster } from '@/components/ui/toaster'
import ErrorBoundary from '@/lib/error-boundary'
import ProvidersErrorFallback from './ProvidersErrorFallback'
import LayoutClient from './layout-client'
import { useState, useEffect } from 'react'

export default function Providers({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  // Always call hooks in the same order
  useEffect(() => {
    setMounted(true);
  }, []);

  // Debug hydration issues
  useEffect(() => {
    if (mounted) {
      console.log('🔍 Providers mounted, client-side rendering active');
    }
  }, [mounted]);

  return (
    <ErrorBoundary 
      fallback={<ProvidersErrorFallback />}
      onError={(error) => {
        console.error('Providers error boundary caught error:', error);
        setError(error.message);
      }}
    >
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <AuthProvider>
          <TenantProvider>
            <ModeProvider>
              <ProfileProvider>
                <MetalRatesProvider>
                  <LayoutClient>
                    {error ? (
                      <div className="flex items-center justify-center min-h-screen">
                        <div className="text-center">
                          <h2 className="text-xl font-semibold text-gray-900 mb-2">Provider Error</h2>
                          <p className="text-gray-600 mb-4">{error}</p>
                          <button 
                            onClick={() => {
                              console.log('Manual refresh triggered from provider error');
                              window.location.reload();
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                          >
                            Refresh Page
                          </button>
                        </div>
                      </div>
                    ) : (
                      children
                    )}
                  </LayoutClient>
                  <Toaster />
                </MetalRatesProvider>
              </ProfileProvider>
            </ModeProvider>
          </TenantProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
} 